<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from './AppIcon.vue'

const scrolled = ref(false)
const mobileOpen = ref(false)
const platformOpen = ref(false)
const route = useRoute()
const router = useRouter()

const searchQuery = ref('')
const searchOpen = ref(false)
const searchInputRef = ref(null)

function openSearch() {
  searchOpen.value = true
  // wait for v-if to render the input
  setTimeout(() => searchInputRef.value?.focus(), 50)
}
function closeSearch() {
  searchOpen.value = false
  searchQuery.value = ''
}
function submitSearch() {
  const q = searchQuery.value.trim()
  if (q) {
    router.push({ path: '/tenders', query: { search: q, page: 1, limit: 20, sort_by: 'published_date' } })
  }
  closeSearch()
}

// Home page has a dark video hero at the top — use light navbar text there
// until the user scrolls (or opens the mobile menu).
// Route name in bid_ui is 'marketing-home' (prefixed to avoid collision with
// bid_ui's own 'Home' route).
const overHero = computed(() => route.name === 'marketing-home' && !scrolled.value && !mobileOpen.value)

// Capabilities grouped by bid lifecycle phase
const preBidLinks = [
  { to: '/tender-insights', icon: 'search', label: 'Tender Insights', desc: 'Instant AI summaries & analysis' },
  { to: '/bid-intelligence', icon: 'trend', label: 'Bid Intelligence', desc: 'Win the right opportunities' },
  { to: '/timeline', icon: 'clock', label: 'Timeline & Alerts', desc: 'Never miss a deadline' }
]
const postBidLinks = [
  { to: '/email-intelligence', icon: 'mail', label: 'Email Intelligence', desc: 'AI drafted client responses' },
  { to: '/claims', icon: 'scale', label: 'Claims Management', desc: 'Evidence-backed claim letters' },
  { to: '/documents', icon: 'folder', label: 'Document Management', desc: 'Secure contract record system' },
  { to: '/chat-assistant', icon: 'chat', label: 'Contract Chat Assistant', desc: 'Chat with your contracts' }
]

const onScroll = () => { scrolled.value = window.scrollY > 24 }
onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))

watch(() => route.fullPath, () => { mobileOpen.value = false; platformOpen.value = false })
</script>

<template>
  <header class="nav" :class="{ scrolled, 'over-hero': overHero }">
    <div class="container nav-inner">
      <RouterLink to="/" class="brand" aria-label="BidSnap AI home">
        <span class="logo-mark">
          <svg viewBox="0 0 64 64" width="34" height="34">
            <defs>
              <linearGradient id="navg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#34f0c0" />
                <stop offset="1" stop-color="#0ea5e9" />
              </linearGradient>
            </defs>
            <!-- BidSnap mark: snap-frame corners capturing a checkmark -->
            <g fill="none" stroke="url(#navg)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M23 15h-4a4 4 0 0 0-4 4v4" />
              <path d="M41 15h4a4 4 0 0 1 4 4v4" />
              <path d="M23 49h-4a4 4 0 0 1-4-4v-4" />
              <path d="M41 49h4a4 4 0 0 0 4-4v-4" />
            </g>
            <path d="M23 33.5l6.5 6.5L44 25" fill="none" stroke="url(#navg)" stroke-width="7" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </span>
        <span class="brand-name">BidSnap<span class="brand-ai"> AI</span></span>
      </RouterLink>

      <nav class="nav-links" :class="{ open: mobileOpen }">
        <div class="dropdown" @mouseenter="platformOpen = true" @mouseleave="platformOpen = false">
          <button class="nav-link dd-trigger" @click="platformOpen = !platformOpen">
            Platform
            <svg class="caret" :class="{ flip: platformOpen }" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          <transition name="dd">
            <div v-show="platformOpen" class="dropdown-panel dropdown-panel--two">
              <div class="dd-col">
                <span class="dd-col-label"><span class="dd-dot pre"></span> Pre-Bid</span>
                <RouterLink v-for="l in preBidLinks" :key="l.to" :to="l.to" class="dd-item">
                  <span class="dd-icon">
                    <AppIcon :name="l.icon" :size="20" />
                  </span>
                  <span>
                    <strong>{{ l.label }}</strong>
                    <small>{{ l.desc }}</small>
                  </span>
                </RouterLink>
              </div>
              <div class="dd-col">
                <span class="dd-col-label"><span class="dd-dot post"></span> Post-Bid</span>
                <RouterLink v-for="l in postBidLinks" :key="l.to" :to="l.to" class="dd-item">
                  <span class="dd-icon">
                    <AppIcon :name="l.icon" :size="20" />
                  </span>
                  <span>
                    <strong>{{ l.label }}</strong>
                    <small>{{ l.desc }}</small>
                  </span>
                </RouterLink>
              </div>
            </div>
          </transition>
        </div>
        <RouterLink to="/timeline" class="nav-link">Solutions</RouterLink>
        <RouterLink to="/documents" class="nav-link">Security</RouterLink>
        <RouterLink to="/chat-assistant" class="nav-link">AI Assistant</RouterLink>
        <RouterLink to="/tenders" class="nav-link nav-tenders-inline">View Tenders</RouterLink>
        <RouterLink to="/api-docs" class="nav-link">API Docs</RouterLink>
        <RouterLink to="/login" class="nav-login-mobile btn btn-ghost">
          <AppIcon name="lock" :size="16" /> Login
        </RouterLink>
        <RouterLink to="/contact" class="nav-cta-mobile btn btn-primary">Request Demo</RouterLink>
      </nav>

      <div class="nav-actions">
        <!-- Tender search box — always visible, submits to /tenders?search= -->
        <form class="nav-search-box" @submit.prevent="submitSearch">
          <AppIcon name="search" :size="15" class="nav-search-icon" />
          <input v-model="searchQuery" class="nav-search-input" type="text" placeholder="Search tenders…"
            @keyup.esc="searchQuery = ''" />
        </form>
        <RouterLink to="/login" class="nav-link nav-login">
          <AppIcon name="lock" :size="16" /> Login
        </RouterLink>
        <RouterLink to="/contact" class="btn btn-primary nav-cta">
          Request Demo
          <AppIcon name="arrow" :size="17" class="arrow" />
        </RouterLink>
        <button class="burger" :class="{ open: mobileOpen }" @click="mobileOpen = !mobileOpen" aria-label="Menu">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: background 0.4s, border-color 0.4s, backdrop-filter 0.4s, padding 0.4s;
  border-bottom: 1px solid transparent;
  padding: 8px 0;
}

.nav.scrolled {
  background: rgba(245, 248, 252, 0.82);
  backdrop-filter: blur(18px) saturate(140%);
  border-bottom-color: var(--line);
  box-shadow: 0 4px 20px rgba(15, 36, 64, 0.05);
}

/* Over the dark video hero: light text */
.nav.over-hero .brand-name {
  color: #fff;
}

.nav.over-hero .nav-link {
  color: rgba(255, 255, 255, 0.86) !important;
}

.nav.over-hero .nav-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

.nav.over-hero .logo-mark {
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.35));
}

/* This element is `class="container nav-inner"`; the marketing `.container`
   reset forces display:block, so re-assert the flex bar at higher specificity. */
.nav .nav-inner {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}

.nav .brand {
  display: flex;
  align-items: center;
  gap: 11px;
}

.nav .logo-mark {
  display: grid;
  place-items: center;
  filter: drop-shadow(0 2px 6px rgba(26, 161, 126, 0.3));
}

.nav .brand-name {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.22rem;
  color: var(--ink-0);
  letter-spacing: -0.02em;
}

.nav .brand-ai {
  background: var(--grad-primary);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* `.nav-links` / `.nav-link` collide with bid_ui's global nav rules
   (dashboard.css + responsive.css hides `.nav-links` under 768px). Prefixing
   with `.nav` raises specificity so the marketing navbar always wins. */
.nav .nav-links {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav .nav-link {
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--ink-1);
  padding: 9px 14px;
  border-radius: 10px;
  cursor: pointer;
  background: none;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.25s, background 0.25s;
}

.nav .nav-link:hover,
.nav .router-link-active.nav-link {
  color: var(--ink-0);
  background: rgba(15, 36, 64, 0.05);
}

.caret {
  transition: transform 0.3s;
}

.caret.flip {
  transform: rotate(180deg);
}

.dropdown {
  position: relative;
}

.dropdown-panel {
  position: absolute;
  top: calc(100% + 12px);
  left: -10px;
  width: 380px;
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  padding: 10px;
  box-shadow: var(--shadow-md);
  display: grid;
  gap: 2px;
}

.dropdown-panel--two {
  width: 600px;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 12px;
}

.dd-col {
  display: grid;
  gap: 2px;
  align-content: start;
}

.dd-col+.dd-col {
  border-left: 1px solid var(--line);
  padding-left: 12px;
}

.dd-col-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-2);
  padding: 6px 12px 8px;
}

.dd-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.dd-dot.pre {
  background: var(--teal);
  box-shadow: 0 0 8px rgba(26, 161, 126, 0.5);
}

.dd-dot.post {
  background: var(--cyan);
  box-shadow: 0 0 8px rgba(47, 134, 179, 0.5);
}

.dd-item {
  display: flex;
  gap: 13px;
  align-items: center;
  padding: 11px 12px;
  border-radius: 12px;
  transition: background 0.25s;
}

.dd-item:hover {
  background: rgba(15, 36, 64, 0.04);
}

.dd-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: rgba(26, 161, 126, 0.10);
  border: 1px solid rgba(26, 161, 126, 0.2);
  color: var(--teal-bright);
  flex: 0 0 auto;
}

.dd-item strong {
  display: block;
  color: var(--ink-0);
  font-family: var(--font-display);
  font-size: 0.92rem;
}

.dd-item small {
  color: var(--ink-2);
  font-size: 0.8rem;
}

.dd-enter-active,
.dd-leave-active {
  transition: opacity 0.22s, transform 0.22s;
}

.dd-enter-from,
.dd-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Tender search box */
.nav-search-box {
  display: flex;
  align-items: center;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(6px);
  padding: 0 14px 0 12px;
  height: 38px;
  gap: 8px;
  transition: border-color 0.2s, background 0.2s;
}

.nav-search-box:focus-within {
  border-color: var(--teal);
  background: rgba(255, 255, 255, 0.95);
}

.nav-search-icon {
  color: var(--ink-2);
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.nav-search-input {
  width: 200px;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--ink-0);
  transition: width 0.25s;
}

.nav-search-input::placeholder {
  color: var(--ink-3);
}

.nav-search-input:focus {
  width: 420px;
}

/* Over hero: light style */
.nav.over-hero .nav-search-box {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.28);
}

.nav.over-hero .nav-search-box:focus-within {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.6);
}

.nav.over-hero .nav-search-input {
  color: #fff;
}

.nav.over-hero .nav-search-input::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.nav.over-hero .nav-search-icon {
  color: rgba(255, 255, 255, 0.7);
}

/* These carry the `.btn` class, so `.marketing-app .btn { display:inline-flex }`
   would otherwise force them visible on desktop — keep `!important` so the
   mobile-only buttons stay hidden until the mobile breakpoint. */
.nav-cta-mobile {
  display: none !important;
}

.nav-login-mobile {
  display: none !important;
}

.nav-login {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.nav-login svg {
  opacity: 0.85;
}

.burger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}

.burger span {
  width: 24px;
  height: 2px;
  background: var(--ink-0);
  border-radius: 2px;
  transition: transform 0.3s, opacity 0.3s;
}

.burger.open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.burger.open span:nth-child(2) {
  opacity: 0;
}

.burger.open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

@media (max-width: 940px) {
  .nav-cta {
    display: none;
  }

  .nav-login {
    display: none;
  }

  .nav-search-box {
    display: none;
  }

  .burger {
    display: flex;
  }

  .nav .nav-links {
    position: fixed;
    inset: 64px 0 auto 0;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    background: rgba(245, 248, 252, 0.99);
    backdrop-filter: blur(20px);
    padding: 18px;
    border-bottom: 1px solid var(--line);
    box-shadow: var(--shadow-md);
    transform: translateY(-120%);
    opacity: 0;
    pointer-events: none;
    transition: transform 0.4s var(--ease), opacity 0.3s;
    max-height: calc(100vh - 64px);
    overflow-y: auto;
  }

  .nav .nav-links.open {
    transform: none;
    opacity: 1;
    pointer-events: auto;
  }

  .nav-login-mobile {
    display: inline-flex !important;
    justify-content: center;
    margin-top: 12px;
  }

  .nav-cta-mobile {
    display: inline-flex !important;
    margin-top: 8px;
  }

  .dropdown-panel {
    position: static;
    width: 100%;
    margin-top: 6px;
  }

  .dropdown-panel--two {
    width: 100%;
    grid-template-columns: 1fr;
  }

  .dd-col+.dd-col {
    border-left: none;
    padding-left: 0;
    border-top: 1px solid var(--line);
    padding-top: 6px;
    margin-top: 4px;
  }

  .nav .nav-link {
    font-size: 1.05rem;
    padding: 12px 14px;
  }
}
</style>
